import React, { useState } from 'react';
import { Modal } from '@leagueplatform/genesis-core';
import { EffectiveTime, InputConfigValue } from '@leagueplatform/dashboard-api';
import { track } from '@leagueplatform/analytics';
import { ParseValueForInput } from '#utils/parse-value-for-input.util.ts';
import { AddEditDataModalContent } from './add-edit-data-modal-content';
import { AddEditDataModalContentContainer } from './add-edit-data-modal-content-container';

export type DefaultValues = {
  observationId?: string;
  value: number | string | null;
  label?: string;
};

type AddEditDataModalProps = {
  modalTrigger: React.ReactNode;
  inputConfig: InputConfigValue;
  metricConfigId: string;
  defaultValues?: DefaultValues[];
  timestamp?: string;
  effectiveDateTime?: EffectiveTime;
  onAddData?: (date: Date) => void;
};

export function AddEditDataModal({
  modalTrigger,
  inputConfig,
  metricConfigId,
  defaultValues,
  timestamp,
  effectiveDateTime,
  onAddData,
}: AddEditDataModalProps) {
  const [modalOpen, setModalOpen] = useState(false);

  React.useEffect(() => {
    if (modalOpen) {
      track('dashboard.screen_loaded.metric_data_entry_screen_metric_type', {
        metric_type: metricConfigId || '',
        metric_id: '',
        metric_status: '',
      });
    }
  }, [metricConfigId, modalOpen]);

  // Construct values object from inputConfig and defaultValues array
  const values = React.useMemo(() => {
    if (!defaultValues || defaultValues.length === 0) {
      return undefined;
    }

    return {
      effectiveDateTime,
      timeStamp: timestamp,
      metrics: defaultValues.map((metric) => ({
        observationId: metric.observationId,
        value: ParseValueForInput(metric.value ?? 0, metric.label),
      })),
    };
  }, [defaultValues, timestamp, effectiveDateTime]);

  return (
    <Modal.Root
      open={modalOpen}
      onOpenChange={() => {
        setModalOpen((previous) => !previous);
      }}
    >
      <Modal.Trigger>{modalTrigger}</Modal.Trigger>
      <AddEditDataModalContentContainer>
        <AddEditDataModalContent
          inputConfig={inputConfig}
          metricConfigId={metricConfigId}
          onSuccessSubmit={() => {
            setModalOpen(false);
          }}
          onAddData={onAddData}
          values={values}
        />
      </AddEditDataModalContentContainer>
    </Modal.Root>
  );
}
